import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
  signal,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { formatDate, formatTime } from '@app/helpers/formatDateTime.helper';
import { createKeyedLoader } from '@app/helpers/loader';
import { connectionToStatus } from '@app/helpers/status.helper';
import type { UserAccount } from '@app/models/account.model';
import { LegalEntityWithUser } from '@app/models/project.models';
import { LegalEntityService } from '@app/services/admin/legal-entity.service';
import { UserService } from '@app/services/admin/user.service';
import { DownloadService } from '@app/services/file/download.service';
import { DecisionCellRendererComponent } from '@app/ui-components/grid-table/decision-cell-renderer/decision-cell-renderer.component';
import { DeleteCellRendererComponent } from '@app/ui-components/grid-table/delete-cell-renderer/delete-cell-renderer.component';
import { DownloadCellRendererComponent } from '@app/ui-components/grid-table/download-cell-renderer/download-cell-renderer.component';
import { customRendererCellStyle } from '@app/ui-components/grid-table/custom-renderer-cell-style';
import { GridTableComponent } from '@app/ui-components/grid-table/grid-table.component';
import { TwoLineCellRendererComponent } from '@app/ui-components/grid-table/two-line-cell-renderer/two-line-cell-renderer.component';
import { TranslateService } from '@ngx-translate/core';
import type {
  ColDef,
  ICellRendererParams,
  IDatasource,
  IGetRowsParams,
} from 'ag-grid-community';
import { tap } from 'rxjs';

type Row = {
  id: string;
  createdAt: string;
  createdDate: string;
  createdTime: string;
  legalEntity: string;
  title: string;
  subtitle: string;
  documents: string;
  docStatus?: 'pending' | 'approved' | 'rejected';
  enabledConnection?: boolean | null;
  legalEntityId: string;
};

@Component({
  selector: 'app-entities-tab',
  standalone: true,
  imports: [CommonModule, FormsModule, GridTableComponent],
  templateUrl: './entities-tab.component.html',
  styleUrl: './entities-tab.component.scss',
})
export class EntitiesTabComponent implements OnInit {
  @Input() user: UserAccount | null = null;

  columns: ColDef[] = [];
  rowModelType: 'infinite' = 'infinite';
  cacheBlockSize = 10;
  maxBlocksInCache = 5;
  rowHeight = 56;
  datasource!: IDatasource;

  private readonly userService = inject(UserService);
  private readonly legalEntityService = inject(LegalEntityService);
  private readonly downloadService = inject(DownloadService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly searchTerm = signal<string>('');
  readonly isLoading = signal<boolean>(false);
  private readonly loader = createKeyedLoader();

  ngOnInit(): void {
    this.buildColumns();
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.buildColumns());
    this.datasource = this.buildDatasource(null);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['user'] && this.userId) {
      this.datasource = this.buildDatasource(this.userId);
    }
  }

  private get userId(): string | null {
    return this.user?.id ?? null;
  }

  onSearch(term: string | undefined): void {
    this.searchTerm.set(term ?? '');
    this.datasource = this.buildDatasource(this.userId);
  }

  private buildColumns(): void {
    this.columns = [
      {
        field: 'createdAt',
        headerName: this.translate.instant('admin.users.table.date'),
        cellRenderer: TwoLineCellRendererComponent,
        cellRendererParams: (param: ICellRendererParams<Row>) => ({
          title: param.data?.createdDate ?? '-',
          subtitle: param.data?.createdTime ?? '',
        }),
        cellStyle: customRendererCellStyle,
        width: 100,
        flex: 0,
        suppressNavigable: true,
        sortable: false,
        filter: false,
      },
      {
        field: 'legalEntity',
        headerName: this.translate.instant('admin.users.table.legalEntity'),
        cellRenderer: TwoLineCellRendererComponent,
        cellRendererParams: (param: ICellRendererParams<Row>) => ({
          title: param.data?.title ?? '',
          subtitle: param.data?.subtitle ?? '',
        }),
        cellStyle: customRendererCellStyle,
        sortable: false,
        filter: false,
      },
      {
        field: 'documents',
        headerName: this.translate.instant('admin.users.table.documents'),
        cellRenderer: DownloadCellRendererComponent,
        cellRendererParams: (param: ICellRendererParams<Row>) => {
          const rowId = param.data?.id ?? null;
          const legalEntityId = param.data?.legalEntityId ?? '';
          const userId = this.user?.id ?? '';

          return {
            actions: {
              fetchList: () =>
                this.loader.trackInProgress(rowId)(
                  this.legalEntityService.getApprovalRequestFiles(
                    legalEntityId,
                    userId
                  )
                ),
              download: (fileId: string) =>
                this.loader.trackInProgress(rowId)(
                  this.downloadService.downloadFileById(fileId)
                ),
              loading: () => this.loader.isInProgress(rowId),
            },
          };
        },
        maxWidth: 140,
        suppressNavigable: true,
        editable: false,
        sortable: false,
        filter: false,
        cellClass: 'text-center',
      },
      {
        field: 'decision',
        headerName: '',
        cellRenderer: DecisionCellRendererComponent,
        cellRendererParams: (param: ICellRendererParams<Row>) => ({
          status: connectionToStatus(param.data?.enabledConnection ?? null),
          actions: {
            approve: (rowId: string) =>
              this.legalEntityService.updateApprovalRequest(rowId, true),
            decline: (rowId: string) =>
              this.legalEntityService.updateApprovalRequest(rowId, false),
          },
        }),
        width: 150,
        flex: 0,
        suppressNavigable: true,
        editable: false,
        sortable: false,
        filter: false,
        cellStyle: {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          gap: '8px',
        },
        cellClass: 'text-center',
      },
      {
        field: 'delete',
        headerName: '',
        cellRenderer: DeleteCellRendererComponent,
        cellRendererParams: (param: ICellRendererParams<Row>) => ({
          status: connectionToStatus(param.data?.enabledConnection ?? null),
          onDelete: (rowId: string) =>
            this.legalEntityService.deleteApprovalRequest(rowId).pipe(
              tap(() => {
                if (param.api?.purgeInfiniteCache) {
                  param.api.purgeInfiniteCache();
                } else if (param.api?.refreshInfiniteCache) {
                  param.api.refreshInfiniteCache();
                }
              })
            ),
        }),
        width: 70,
        flex: 0,
        suppressNavigable: true,
        editable: false,
        sortable: false,
        filter: false,
        cellClass: 'text-center',
      },
    ];
  }

  private buildDatasource(userId: string | null): IDatasource {
    return {
      rowCount: undefined,
      getRows: (param: IGetRowsParams) => {
        if (!userId) {
          param.successCallback([], 0);
          return;
        }

        this.isLoading.set(true);
        const term = this.searchTerm().trim().toLowerCase();

        this.userService.getAllApprovalRequests(userId).subscribe({
          next: (res: LegalEntityWithUser[] | any) => {
            const requests: LegalEntityWithUser[] = Array.isArray(res)
              ? res
              : [];

            const list = requests;

            const rows: Row[] = list.map((req) => {
              const entity = req.legalEntity;
              const address = (entity?.address ?? {}) as any;
              const part = (v?: string | null) =>
                (v ?? '').toString().trim() || '';

              const subtitle = [
                part(address.doorOrFloor),
                part(address.houseNumber),
                [part(address.publicSpace), part(address.publicSpaceType)]
                  .filter(Boolean)
                  .join(' '),
                part(address.city),
                part(address.zipCode),
                part(address.countryCode),
              ]
                .filter(Boolean)
                .join(', ');

              const title = [
                entity?.name,
                entity?.taxNumber ? `(${entity.taxNumber})` : '',
              ]
                .filter(Boolean)
                .join(' ');

              return {
                id: req.id,
                createdAt: formatDate(req.creationDate),
                createdDate: formatDate(req.creationDate),
                createdTime: formatTime(req.creationDate),
                legalEntity: title || '-',
                title: title || '-',
                subtitle,
                documents: 'View',
                docStatus: 'pending',
                enabledConnection: req.enabledConnection ?? null,
                legalEntityId: req.pczrtLegalEntityId,
              };
            });

            const filtered = term
              ? rows.filter((row) =>
                  (row.legalEntity + ' ' + row.subtitle)
                    .toLowerCase()
                    .includes(term)
                )
              : rows;

            const page = filtered.slice(param.startRow, param.endRow);
            param.successCallback(page, filtered.length);
          },
          error: () => param.successCallback([], 0),
          complete: () => this.isLoading.set(false),
        });
      },
    };
  }
}
